import { costQuantity } from './answers';
import type { TemplateGraph } from './graph';
import type { RuntimeNode, RuntimeTree } from './instances';
import { multiplyCents, parseAmountOverrideToCents, parseAmountToCents } from './money';
import type { AnswerKey, AnswerSet, CostGroupId, NodeId } from './types';
import type { ChosenOption, NodeVisibility, VisibilityResult } from './visibility';

/**
 * CostAggregator — kosten per urgentie en per kostengroep, bottom-up.
 *
 * Twee regels maken het exact (`graph-semantics.md` §5):
 * 1. staat de rand op een `number`-vraag, dan is het bedrag een eenheidsprijs
 *    maal het ingevulde aantal;
 * 2. de totalen op een knoop zijn **subtree-totalen**: eigen kosten plus alle
 *    afstammelingen. Dat geldt ook voor de uitsplitsing per kostengroep.
 *
 * En twee regels wijken bewust af van legacy (§10), exact zoals de API:
 * 3. de hoeveelheid telt met decimalen; het bedrag wordt afgerond op hele
 *    centen, half naar boven (`multiplyCents`);
 * 4. een eigen bedrag van de inspecteur op een antwoord vervangt voor die
 *    urgentie het berekende bedrag van het hele antwoord (`countChosenAmounts`).
 */

/** Bucket voor bedragen zonder kostengroep; de echte groepen zijn 1-based. */
export const UNGROUPED_COST_GROUP_ID: CostGroupId = 0;

/** Bedragen in hele centen. */
export interface CostAmounts {
    readonly direct: number;
    readonly term: number;
    readonly improvement: number;
}

export interface NodeCosts {
    readonly nodeId: NodeId;
    /** Alleen de gevolgde randen van deze knoop. */
    readonly own: CostAmounts;
    readonly ownByCostGroup: ReadonlyMap<CostGroupId, CostAmounts>;
    /** Deze knoop plus alle afstammelingen. */
    readonly subtree: CostAmounts;
    readonly subtreeByCostGroup: ReadonlyMap<CostGroupId, CostAmounts>;
    /**
     * Per gevolgde rand van deze knoop, op dezelfde positie als in
     * `NodeVisibility.chosen`: het bedrag dat die rand telt, met hoeveelheid en
     * eigen bedragen verwerkt. Dit is het bedrag van een afgeleide bevinding;
     * `own` is er de som van.
     */
    readonly chosen: readonly CostAmounts[];
}

export interface CostTotals {
    readonly nodes: ReadonlyMap<NodeId, NodeCosts>;
    readonly total: CostAmounts;
    readonly totalByCostGroup: ReadonlyMap<CostGroupId, CostAmounts>;
}

const ZERO: CostAmounts = { direct: 0, term: 0, improvement: 0 };

export function emptyAmounts(): CostAmounts {
    return ZERO;
}

export function addAmounts(a: CostAmounts, b: CostAmounts): CostAmounts {
    return {
        direct: a.direct + b.direct,
        term: a.term + b.term,
        improvement: a.improvement + b.improvement,
    };
}

function isZeroAmounts(amounts: CostAmounts): boolean {
    return amounts.direct === 0 && amounts.term === 0 && amounts.improvement === 0;
}

/** Rekent alle knopen door en telt ze bottom-up op. */
export function aggregateNodeCosts(
    graph: TemplateGraph,
    answers: AnswerSet,
    tree: RuntimeTree,
    visibility: VisibilityResult,
): CostTotals {
    const chosenByNode = countChosenAmounts(graph, answers, tree, visibility);
    const nodes = new Map<NodeId, NodeCosts>();

    for (let index = tree.order.length - 1; index >= 0; index -= 1) {
        const nodeId = tree.order[index] as NodeId;
        const runtimeNode = tree.nodes.get(nodeId) as RuntimeNode;
        const node = visibility.nodes.get(nodeId) as NodeVisibility;
        const chosen = chosenByNode.get(nodeId) ?? [];

        let own = ZERO;
        const ownByCostGroup = new Map<CostGroupId, CostAmounts>();

        node.chosen.forEach((entry, position) => {
            const amounts = chosen[position] ?? ZERO;

            if (isZeroAmounts(amounts)) {
                return;
            }

            own = addAmounts(own, amounts);
            const groupId = entry.option.cost_group_id ?? UNGROUPED_COST_GROUP_ID;

            ownByCostGroup.set(groupId, addAmounts(ownByCostGroup.get(groupId) ?? ZERO, amounts));
        });

        let subtree = own;
        const subtreeByCostGroup = new Map<CostGroupId, CostAmounts>(ownByCostGroup);

        for (const childId of runtimeNode.childIds) {
            const child = nodes.get(childId);

            if (!child) {
                continue;
            }

            subtree = addAmounts(subtree, child.subtree);

            for (const [groupId, amounts] of child.subtreeByCostGroup) {
                subtreeByCostGroup.set(
                    groupId,
                    addAmounts(subtreeByCostGroup.get(groupId) ?? ZERO, amounts),
                );
            }
        }

        nodes.set(nodeId, { nodeId, own, ownByCostGroup, subtree, subtreeByCostGroup, chosen });
    }

    let total = ZERO;
    const totalByCostGroup = new Map<CostGroupId, CostAmounts>();

    for (const rootId of tree.rootIds) {
        const root = nodes.get(rootId);

        if (!root) {
            continue;
        }

        total = addAmounts(total, root.subtree);

        for (const [groupId, amounts] of root.subtreeByCostGroup) {
            totalByCostGroup.set(
                groupId,
                addAmounts(totalByCostGroup.get(groupId) ?? ZERO, amounts),
            );
        }
    }

    return { nodes, total, totalByCostGroup };
}

/**
 * Het bedrag dat elke gevolgde rand telt, per knoop in randvolgorde.
 *
 * Een eigen bedrag van de inspecteur vervangt voor die urgentie het berekende
 * bedrag van het **hele antwoord**, en telt dus één keer: op de eerste gevolgde
 * rand van dat antwoord in boomvolgorde (knopen in pre-order, daarbinnen in
 * randvolgorde). Op elke volgende rand van hetzelfde antwoord — een tweede
 * gekozen optie, of dezelfde vraag in een latere knoop — telt die urgentie 0.
 * Niet aangepaste urgenties houden hun berekende bedrag. Exact
 * `CostAggregator::withOverrides()` in de API.
 */
function countChosenAmounts(
    graph: TemplateGraph,
    answers: AnswerSet,
    tree: RuntimeTree,
    visibility: VisibilityResult,
): Map<NodeId, CostAmounts[]> {
    const booked = new Set<AnswerKey>();
    const amounts = new Map<NodeId, CostAmounts[]>();

    for (const nodeId of tree.order) {
        const node = visibility.nodes.get(nodeId) as NodeVisibility;

        amounts.set(
            nodeId,
            node.chosen.map((chosen) => countedAmounts(graph, answers, chosen, booked)),
        );
    }

    return amounts;
}

function countedAmounts(
    graph: TemplateGraph,
    answers: AnswerSet,
    chosen: ChosenOption,
    booked: Set<AnswerKey>,
): CostAmounts {
    const answer = answers[chosen.questionKey];
    const question = graph.questionsById.get(chosen.questionId);
    const quantity = question ? costQuantity(question, answer) : 1;
    const calculated: CostAmounts = {
        direct: multiplyCents(parseAmountToCents(chosen.option.direct_cost), quantity),
        term: multiplyCents(parseAmountToCents(chosen.option.term_cost), quantity),
        improvement: multiplyCents(parseAmountToCents(chosen.option.improvement_cost), quantity),
    };
    const overrides = {
        direct: parseAmountOverrideToCents(answer?.direct_cost),
        term: parseAmountOverrideToCents(answer?.term_cost),
        improvement: parseAmountOverrideToCents(answer?.improvement_cost),
    };

    if (overrides.direct === null && overrides.term === null && overrides.improvement === null) {
        return calculated;
    }

    const first = !booked.has(chosen.questionKey);

    booked.add(chosen.questionKey);

    const count = (override: number | null, amount: number): number =>
        override === null ? amount : first ? override : 0;

    return {
        direct: count(overrides.direct, calculated.direct),
        term: count(overrides.term, calculated.term),
        improvement: count(overrides.improvement, calculated.improvement),
    };
}
